import type { QuoteLineItem } from "@/hooks/useQuickQuotes";

/**
 * Determina se um line item é componente "filho" de um kit pai.
 * Componentes de kit não devem aparecer como linha independente no PDF
 * nem na página pública — eles aparecem apenas indentados sob o kit pai
 * (sem preço, prefixados com ├ / └).
 *
 * Estratégias (em ordem):
 * 1. Campo explícito `parent_kit_line_id` (preferido, novos registros).
 * 2. Fallback retrocompatível: equipment com unit_price=0 logo após um kit.
 */
export function isKitComponent(
  it: QuoteLineItem,
  idx: number,
  all: QuoteLineItem[],
): boolean {
  if ((it as any).parent_kit_line_id) return true;
  if (it.type !== "equipment" || Number(it.unit_price) > 0) return false;
  for (let i = idx - 1; i >= 0; i--) {
    if (all[i].type === "kit") return true;
    if (all[i].type === "equipment" && Number(all[i].unit_price) > 0) {
      return false;
    }
  }
  return false;
}

export interface GroupedLineItem {
  /** Linha "pai" — kit ou item simples. */
  item: QuoteLineItem;
  /** Componentes do kit (vazio quando não é kit). */
  children: QuoteLineItem[];
}

/**
 * Agrupa line_items associando cada componente de kit ao seu kit pai
 * (na ordem em que aparecem). Itens que não são kit têm `children` vazio.
 */
export function groupLineItems(items: QuoteLineItem[]): GroupedLineItem[] {
  const result: GroupedLineItem[] = [];
  items.forEach((it, idx) => {
    if (isKitComponent(it, idx, items)) {
      // Achar o último pai (kit) já adicionado em result.
      for (let i = result.length - 1; i >= 0; i--) {
        if (result[i].item.type === "kit") {
          result[i].children.push(it);
          return;
        }
      }
      // Sem pai identificável → cai como linha solta (raro/legado).
      result.push({ item: it, children: [] });
    } else {
      result.push({ item: it, children: [] });
    }
  });
  return result;
}

/**
 * Remove itens-raiz com total <= 0 (não devem aparecer para o cliente
 * no PDF nem na página pública). Componentes de kit são preservados
 * — eles têm total=0 por design e são exibidos indentados sob o kit pai.
 *
 * Aplicar SEMPRE antes de `groupLineItems` para garantir que kits
 * sem valor sumam junto com seus filhos.
 */
export function filterClientVisibleItems(
  items: QuoteLineItem[],
): QuoteLineItem[] {
  // Marca componentes usando os índices ORIGINAIS (isKitComponent depende
  // do contexto sequencial).
  const componentFlags = items.map((it, idx) =>
    isKitComponent(it, idx, items),
  );
  return items.filter((it, idx) => {
    if (componentFlags[idx]) return true;
    return Number(it.total || 0) > 0;
  });
}
